import { Accordion, AccordionItem, InlineLoading } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  ActivityFeedPanel,
  isActivityFeedPanelOpenAtom,
} from 'components/activity-feed-panel';
import { EmptyState } from 'components/empty-state';
import { DualSidebarLayout } from 'components/layout/dual-sidebar-layout';
import { PageHeader } from 'components/page-header';
import { WorkspaceShellHeader } from 'components/workspace-shell-header';
import type { InterestWorkspaceResponse } from 'types/api-responses';

import {
  closeWorkspaceOverlaysAtom,
  tasksPanelOpenAtom,
  workspaceAtomFamily,
} from './atoms';
import { ComparisonTable } from './comparison-table/comparison-table';
import { ConfirmInterestPanel } from './confirmation-workflow/confirm-interest-panel';
import { CustomLossWarningModal } from './confirmation-workflow/custom-loss-warning-modal';
import { WorkspaceHeader } from './header';
import { PromoteModal } from './promote-modal';
import { PromotionPendingBanner } from './promotion-pending-banner';
import { ReconcileModal } from './reconcile-modal';
import { SlotDetailPanel } from './slot-detail/slot-detail-panel';
import { TasksPanel } from './tasks-panel';
import { UnconfirmModal } from './unconfirm-modal';
import { ReconciliationSummary } from './reconciliation-summary';
import { useEntityPromotionChannel } from './use-entity-promotion-channel';
import { WorkspaceConfirmationProvider } from './workspace-confirmation-context';
import { WorkspaceNameEditor } from './workspace-name-editor';
import { EntitySlotsView } from './entity-slots-view';
import styles from './page.module.scss';

type RouteParams = { id: string };

const EMPTY_BREADCRUMBS: [string, ReactNode][] = [];

/**
 * Inner component that performs the workspace fetch for a known-valid id.
 *
 * Split out from `WorkspaceShowPage` so the data hook is never called
 * conditionally. Branches on `!data` (not `loading`) so a refetch keeps the
 * loaded shell mounted instead of flashing the loading state.
 *
 * @param {object} props - Component props.
 * @param {number} props.id - Numeric workspace id parsed from the URL.
 * @return {JSX.Element} The loading / error / loaded shell content.
 */
/**
 * Subscribes the loaded workspace to its entity Pusher channel. Rendered only
 * inside the loaded subtree so the hook runs unconditionally for a known
 * workspace (rules-of-hooks-safe), and renders nothing.
 *
 * @param {object} props - Component props.
 * @param {InterestWorkspaceResponse} props.workspace - The loaded workspace.
 * @return {null} Nothing — side-effect only.
 */
const PromotionSync = ({
  workspace,
}: {
  workspace: InterestWorkspaceResponse;
}): null => {
  useEntityPromotionChannel(workspace);
  return null;
};

const WorkspaceShowContent = ({ id }: { id: number }): JSX.Element => {
  const { data } = useAtomValue(workspaceAtomFamily(id));
  const tasksOpen = useAtomValue(tasksPanelOpenAtom);
  const activityOpen = useAtomValue(isActivityFeedPanelOpenAtom);
  const closeOverlays = useSetAtom(closeWorkspaceOverlaysAtom);
  const history = useHistory();

  // The shared `ActivityFeedPanelOpenButton` opens the drawer directly, so the
  // "opening Activity closes the other overlays" half of the mutual-exclusion
  // rule is wired here: when the drawer opens, clear everything else but keep
  // the drawer itself open (`'activity'`). The reverse half — other overlays
  // closing the drawer — lives in the open atoms in `./atoms`.
  useEffect(() => {
    if (activityOpen) closeOverlays('activity');
  }, [activityOpen, closeOverlays]);

  if (!data) {
    return (
      <PageHeader
        breadcrumbs={EMPTY_BREADCRUMBS}
        title={<InlineLoading description="Loading workspace…" />}
        actions={null}
        stickyHeader
      />
    );
  }

  if ('error' in data) {
    return (
      <>
        <PageHeader
          breadcrumbs={EMPTY_BREADCRUMBS}
          title=""
          actions={null}
          stickyHeader
        />
        <EmptyState headerText="Workspace not found" />
      </>
    );
  }

  const workspace = data;
  return (
    <WorkspaceConfirmationProvider>
      <PromotionSync workspace={workspace} />
      <div className={styles.bodyShell}>
        <DualSidebarLayout
          right={{
            content: tasksOpen ? <TasksPanel /> : null,
            isOpen: tasksOpen,
          }}
        >
          {/* Stable id (not the hashed CSS-module class) so the Slot Details
              SidePanel's `slideIn` can push this content left when it opens —
              `.mainStack` has a 0 default margin so it restores cleanly on
              close (unlike `.bodyShell`, whose negative margin would leave a
              gap). */}
          <div className={styles.mainStack} id="ic-workspace-main">
            <WorkspaceShellHeader
              title={<WorkspaceNameEditor workspace={workspace} />}
              onBack={() => history.push(`/entities/${workspace.entityId}`)}
              actions={<WorkspaceHeader workspace={workspace} />}
            />
            <ReconciliationSummary workspace={workspace} />
            <PromotionPendingBanner />
            <ComparisonTable />
            <Accordion>
              <AccordionItem title="Entity Slots — Certified Interest View">
                <EntitySlotsView entityId={workspace.entityId} />
              </AccordionItem>
            </Accordion>
          </div>
        </DualSidebarLayout>
      </div>
      <ConfirmInterestPanel />
      <CustomLossWarningModal />
      <SlotDetailPanel />
      <ActivityFeedPanel
        modelType="InterestCertification::Workspace"
        modelId={workspace.id}
        title="Activity"
        subtitle={workspace.name}
        commentsHref=""
      />
      <PromoteModal />
      <ReconcileModal />
      <UnconfirmModal />
    </WorkspaceConfirmationProvider>
  );
};

/**
 * Top-level page for the interest-certification workspace shell.
 *
 * Reads `:id` from the URL, fetches the workspace via `workspaceAtomFamily`,
 * and renders the shell chrome (dark `WorkspaceShellHeader` +
 * `ReconciliationSummary` + `ComparisonTable`).
 *
 * @return {JSX.Element} The rendered workspace shell page.
 */
export const WorkspaceShowPage = (): JSX.Element => {
  const { id: idParam } = useParams<RouteParams>();
  const id = Number(idParam);

  // Guard against a hand-edited URL with a non-numeric :id. Without this the
  // atomFamily would allocate a cache slot for NaN and fire a fetch for
  // /interest_certification/workspaces/NaN.
  if (!Number.isFinite(id)) {
    return (
      <>
        <PageHeader
          breadcrumbs={EMPTY_BREADCRUMBS}
          title=""
          actions={null}
          stickyHeader
        />
        <EmptyState headerText="Workspace not found" />
      </>
    );
  }

  return <WorkspaceShowContent id={id} />;
};
